import { Forum, Hash, Link2, Plus, Trash2, Volume2 } from '@/components/ui/icons';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import {
  createCategorySchema,
  createChannelSchema,
  createServerSchema,
  LIMITS,
  normalizeChannelName,
  SLOW_MODE_OPTIONS,
  type CategoryDTO,
  type ChannelKind,
  type InvitePreviewDTO,
} from '@clanfire/shared';
import { errorMessage } from '@/api/client';
import { categoryApi, channelApi, inviteApi, serverApi } from '@/api/endpoints';
import { ServerIcon } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Input, Select, TextArea } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useSubmit, validate } from '@/hooks/useForm';
import { formatSlowMode } from '@/lib/slowMode';
import { cn, initials } from '@/lib/utils';
import { useAuth } from '@/stores/auth';
import { useServerCategories, useServers } from '@/stores/servers';
import { confirm, toast } from '@/stores/ui';
import { ChannelPermissions } from './ChannelPermissions';
import { ImagePicker } from './ImagePicker';

/* ------------------------------------------------------------------ */
/* Create server                                                       */
/* ------------------------------------------------------------------ */

export function CreateServerModal({ onClose }: { onClose: () => void }) {
  const me = useAuth((s) => s.user);
  const [name, setName] = useState(`${me?.displayName ?? 'My'}'s server`);
  const [icon, setIcon] = useState<Blob | null>(null);
  const { loading, error, fields, setFields, run } = useSubmit();
  const navigate = useNavigate();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const { data, fields: invalid } = validate(createServerSchema, { name });
    if (!data) return setFields(invalid);
    const server = await run(() => serverApi.create(data.name, icon));
    if (server) {
      useServers.getState().upsertServer(server);
      onClose();
      navigate(`/app/${server.id}`);
      toast.success(`${server.name} created.`);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Create your server"
      description="Give your server a name and an icon. You can change both later."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="create-server" loading={loading}>
            Create server
          </Button>
        </>
      }
    >
      <form id="create-server" onSubmit={submit} className="flex flex-col gap-5">
        <ImagePicker currentUrl={null} fallback={<span className="text-2xl font-bold text-fg-muted">{initials(name || '?')}</span>} onChange={setIcon} />
        <Input label="Server name" value={name} onChange={(e) => setName(e.target.value)} error={fields.name} maxLength={100} data-autofocus required />
        {error && <p className="text-sm text-danger" role="alert">{error}</p>}
      </form>
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/* Join server                                                         */
/* ------------------------------------------------------------------ */

function parseInviteCode(input: string): string {
  const trimmed = input.trim();
  const match = /\/invite\/([A-Za-z0-9-]+)/.exec(trimmed);
  return match ? match[1]! : trimmed;
}

export function JoinServerModal({ onClose, initialCode }: { onClose: () => void; initialCode?: string }) {
  const [value, setValue] = useState(initialCode ?? '');
  const [preview, setPreview] = useState<InvitePreviewDTO | null>(null);
  const { loading, error, run, setError } = useSubmit();
  const navigate = useNavigate();

  const lookup = async (e: FormEvent) => {
    e.preventDefault();
    const code = parseInviteCode(value);
    if (!/^[A-Za-z0-9-]{3,32}$/.test(code)) return setError('Enter a valid invite link or code, e.g. https://clanfire.example/invite/welcome');
    const result = await run(() => inviteApi.preview(code));
    if (result) setPreview(result);
  };

  const join = async () => {
    if (!preview) return;
    if (preview.alreadyMember) {
      onClose();
      return navigate(`/app/${preview.server.id}`);
    }
    const server = await run(() => inviteApi.accept(preview.code));
    if (server) {
      useServers.getState().upsertServer(server);
      onClose();
      navigate(`/app/${server.id}`);
      toast.success(`Welcome to ${server.name}!`);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Join a server"
      description="Enter an invite below to join an existing server."
      footer={
        preview ? (
          <>
            <Button variant="ghost" onClick={() => setPreview(null)}>
              Back
            </Button>
            <Button onClick={() => void join()} loading={loading}>
              {preview.alreadyMember ? 'Open server' : `Join ${preview.server.name}`}
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" form="join-server" loading={loading}>
              Find server
            </Button>
          </>
        )
      }
    >
      {preview ? (
        <div className="flex items-center gap-4 rounded-xl bg-sunken p-4">
          <ServerIcon server={preview.server} size={56} />
          <div className="min-w-0">
            <p className="truncate text-lg font-bold">{preview.server.name}</p>
            <p className="text-sm text-fg-muted">
              {preview.onlineCount} online · {preview.memberCount} members
            </p>
            {preview.alreadyMember && <p className="mt-1 text-xs font-semibold text-success">You're already a member.</p>}
          </div>
        </div>
      ) : (
        <form id="join-server" onSubmit={lookup} className="flex flex-col gap-3">
          <Input label="Invite link" value={value} onChange={(e) => setValue(e.target.value)} placeholder="https://…/invite/welcome or welcome" data-autofocus leading={<Link2 size={16} />} />
          <p className="text-xs text-fg-muted">
            Invites look like <code className="rounded bg-hover px-1">welcome</code> or <code className="rounded bg-hover px-1">{location.origin}/invite/welcome</code>
          </p>
        </form>
      )}
      {error && (
        <p className="mt-3 text-sm text-danger" role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/* Channels                                                            */
/* ------------------------------------------------------------------ */

export function CreateChannelModal({
  serverId,
  initialType,
  initialCategoryId,
  onClose,
}: {
  serverId: string;
  initialType: ChannelKind;
  initialCategoryId?: string | null;
  onClose: () => void;
}) {
  const categories = useServerCategories(serverId);
  const [type, setType] = useState<ChannelKind>(initialType);
  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState<string>(initialCategoryId ?? '');
  const { loading, error, fields, setFields, run } = useSubmit();
  const navigate = useNavigate();
  const preview = normalizeChannelName(name, type);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const { data, fields: invalid } = validate(createChannelSchema, { name, type });
    if (!data) return setFields(invalid);
    const channel = await run(() => serverApi.createChannel(serverId, { name: data.name, type: data.type, categoryId: categoryId || null }));
    if (channel) {
      useServers.getState().upsertChannel(channel);
      onClose();
      navigate(`/app/${serverId}/${channel.id}`);
    }
  };

  const typeCard = (value: ChannelKind, icon: React.ReactNode, label: string, desc: string) => (
    <label
      className={cn(
        'flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition-colors',
        type === value ? 'border-accent bg-accent-soft' : 'border-line bg-sunken hover:border-line-strong',
      )}
    >
      <input type="radio" name="channel-type" value={value} checked={type === value} onChange={() => setType(value)} className="sr-only" />
      <span className="text-fg-soft">{icon}</span>
      <span className="flex-1">
        <span className="block font-semibold">{label}</span>
        <span className="block text-xs text-fg-muted">{desc}</span>
      </span>
      <span className={cn('h-4 w-4 rounded-full border-2', type === value ? 'border-accent bg-accent' : 'border-line-strong')} />
    </label>
  );

  return (
    <Modal
      open
      onClose={onClose}
      title="Create channel"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="create-channel" loading={loading} disabled={!preview}>
            Create channel
          </Button>
        </>
      }
    >
      <form id="create-channel" onSubmit={submit} className="flex flex-col gap-4">
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-2 text-xs font-bold tracking-wide text-fg-soft uppercase">Channel type</legend>
          {typeCard('TEXT', <Hash size={22} />, 'Text', 'Messages, images, links and emoji')}
          {typeCard('VOICE', <Volume2 size={22} />, 'Voice', 'Talk with voice, video and screen sharing')}
          {typeCard('FORUM', <Forum size={22} />, 'Forum', 'Posts with titles and tags, for questions and longer topics')}
        </fieldset>
        <Input
          label="Channel name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={type === 'VOICE' ? 'General' : type === 'FORUM' ? 'help' : 'new-channel'}
          leading={type === 'VOICE' ? <Volume2 size={16} /> : type === 'FORUM' ? <Forum size={16} /> : <Hash size={16} />}
          error={fields.name}
          hint={type !== 'VOICE' && name && preview !== name ? `Will be created as #${preview || '…'}` : undefined}
          maxLength={100}
          data-autofocus
        />
        {categories.length > 0 && <CategorySelect categories={categories} value={categoryId} onChange={setCategoryId} />}
        {error && <p className="text-sm text-danger" role="alert">{error}</p>}
      </form>
    </Modal>
  );
}

export function ChannelSettingsModal({ channelId, onClose }: { channelId: string; onClose: () => void }) {
  const channel = useServers((s) => s.channels[channelId]);
  const [tab, setTab] = useState<'overview' | 'permissions'>('overview');
  const [name, setName] = useState(channel?.name ?? '');
  const [topic, setTopic] = useState(channel?.topic ?? '');
  const [categoryId, setCategoryId] = useState(channel?.categoryId ?? '');
  const [slowMode, setSlowMode] = useState(channel?.slowModeSeconds ?? 0);
  const [tags, setTags] = useState(() => (channel?.forumTags ?? []).map((t) => ({ ...t, emoji: t.emoji ?? '' })));
  const categories = useServerCategories(channel?.serverId);
  const { loading, error, fields, run } = useSubmit();
  const navigate = useNavigate();
  if (!channel) return null;

  const save = async (e: FormEvent) => {
    e.preventDefault();
    const updated = await run(() =>
      channelApi.update(channelId, {
        name,
        ...(channel.type === 'TEXT' || channel.type === 'FORUM' ? { topic: topic.trim() || null } : {}),
        ...(channel.type === 'FORUM'
          ? { forumTags: tags.filter((t) => t.name.trim()).map((t) => ({ ...(t.id && { id: t.id }), name: t.name.trim(), emoji: t.emoji.trim() || null })) }
          : {}),
        ...((categoryId || null) !== channel.categoryId ? { categoryId: categoryId || null } : {}),
        ...(channel.type === 'TEXT' && slowMode !== channel.slowModeSeconds ? { slowModeSeconds: slowMode } : {}),
      }),
    );
    if (updated) {
      useServers.getState().upsertChannel(updated);
      toast.success('Channel updated.');
      onClose();
    }
  };

  const remove = async () => {
    const ok = await confirm({
      title: `Delete ${channel.type === 'VOICE' ? '' : '#'}${channel.name}?`,
      body: 'This permanently deletes the channel and all of its messages. This cannot be undone.',
      confirmLabel: 'Delete channel',
      danger: true,
    });
    if (!ok) return;
    try {
      await channelApi.remove(channelId);
      useServers.getState().removeChannel(channelId);
      onClose();
      navigate(`/app/${channel.serverId}`);
      toast.success('Channel deleted.');
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Channel settings"
      footer={
        tab === 'overview' ? (
          <>
            <Button variant="ghost" className="mr-auto text-danger!" onClick={() => void remove()}>
              Delete channel
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" form="channel-settings" loading={loading}>
              Save changes
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>Done</Button>
        )
      }
    >
      <div role="tablist" aria-label="Channel settings" className="mb-4 flex gap-1 rounded-full bg-sunken p-1">
        {(['overview', 'permissions'] as const).map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className={cn('flex-1 rounded-full px-3 py-1.5 text-sm font-semibold transition-colors', tab === t ? 'bg-surface text-fg shadow-sm' : 'text-fg-muted hover:text-fg')}
          >
            {t === 'overview' ? 'Overview' : 'Permissions'}
          </button>
        ))}
      </div>
      {tab === 'permissions' && <ChannelPermissions channel={channel} />}
      {tab === 'overview' && (
        <form id="channel-settings" onSubmit={save} className="flex flex-col gap-4">
          <Input
            label="Channel name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            leading={channel.type === 'VOICE' ? <Volume2 size={16} /> : channel.type === 'FORUM' ? <Forum size={16} /> : <Hash size={16} />}
            error={fields.name}
            maxLength={100}
            data-autofocus
          />
          {channel.type === 'TEXT' && (
            <TextArea label="Topic" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Let everyone know what this channel is about" maxLength={1024} error={fields.topic} />
          )}
          {channel.type === 'FORUM' && (
            <>
              <TextArea label="Guidelines" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Shown above the posts: what belongs here, and how to post" maxLength={1024} error={fields.topic} />
              <ForumTagsEditor tags={tags} onChange={setTags} />
            </>
          )}
          <CategorySelect categories={categories} value={categoryId} onChange={setCategoryId} />
          {channel.type === 'TEXT' && (
            <Select
              label="Slow mode"
              hint="How long members wait between messages. People who manage messages aren't slowed down."
              value={String(slowMode)}
              onChange={(e) => setSlowMode(Number(e.target.value))}
            >
              {SLOW_MODE_OPTIONS.map((seconds) => (
                <option key={seconds} value={seconds}>
                  {seconds === 0 ? 'Off' : formatSlowMode(seconds)}
                </option>
              ))}
            </Select>
          )}
          {error && <p className="text-sm text-danger" role="alert">{error}</p>}
        </form>
      )}
    </Modal>
  );
}

type EditableTag = { id: string; name: string; emoji: string };

/** A forum's tags: an emoji and a name each. */
function ForumTagsEditor({ tags, onChange }: { tags: EditableTag[]; onChange: (tags: EditableTag[]) => void }) {
  const update = (i: number, patch: Partial<EditableTag>) => onChange(tags.map((t, j) => (j === i ? { ...t, ...patch } : t)));
  return (
    <fieldset>
      <legend className="mb-1 pl-1 text-[13px] font-semibold text-fg-soft">Tags</legend>
      <p className="mb-2 pl-1 text-xs text-fg-muted">Posts can have up to {LIMITS.forumPostTagsMax}, and people can filter the forum by them.</p>
      <ul className="flex flex-col gap-2">
        {tags.map((tag, i) => (
          <li key={tag.id || `new-${i}`} className="flex items-center gap-2">
            <input
              aria-label="Tag emoji"
              value={tag.emoji}
              onChange={(e) => update(i, { emoji: e.target.value })}
              placeholder="🏷️"
              maxLength={8}
              className="h-10 w-12 rounded-control border border-line bg-sunken text-center"
            />
            <input
              aria-label="Tag name"
              value={tag.name}
              onChange={(e) => update(i, { name: e.target.value })}
              placeholder="Question"
              maxLength={LIMITS.forumTagNameMax}
              className="h-10 min-w-0 flex-1 rounded-control border border-line bg-sunken px-3"
            />
            <Button variant="ghost" size="sm" aria-label={`Remove ${tag.name || 'this tag'}`} icon={<Trash2 size={15} />} onClick={() => onChange(tags.filter((_, j) => j !== i))} />
          </li>
        ))}
      </ul>
      {tags.length < LIMITS.forumTagsMax && (
        <Button variant="secondary" size="sm" className="mt-2" icon={<Plus size={15} />} onClick={() => onChange([...tags, { id: '', name: '', emoji: '' }])}>
          Add a tag
        </Button>
      )}
    </fieldset>
  );
}

function CategorySelect({ categories, value, onChange }: { categories: CategoryDTO[]; value: string; onChange: (id: string) => void }) {
  return (
    <Select label="Category" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">No category</option>
      {categories.map((c) => (
        <option key={c.id} value={c.id}>
          {c.name}
        </option>
      ))}
    </Select>
  );
}

/** Create a category, or rename/delete an existing one. */
export function CategoryModal({ serverId, categoryId, onClose }: { serverId: string; categoryId?: string; onClose: () => void }) {
  const existing = useServers((s) => (categoryId ? s.categories[categoryId] : undefined));
  const [name, setName] = useState(existing?.name ?? '');
  const { loading, error, fields, setFields, run } = useSubmit();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const { data, fields: invalid } = validate(createCategorySchema, { name });
    if (!data) return setFields(invalid);
    const category = await run(() => (existing ? categoryApi.rename(existing.id, data.name) : serverApi.createCategory(serverId, data.name)));
    if (category) {
      useServers.getState().upsertCategory(category);
      toast.success(existing ? 'Category renamed.' : 'Category created.');
      onClose();
    }
  };

  const remove = async () => {
    if (!existing) return;
    const ok = await confirm({
      title: `Delete ${existing.name}?`,
      body: 'The category is removed. Its channels are kept and moved out of it.',
      confirmLabel: 'Delete category',
      danger: true,
    });
    if (!ok) return;
    try {
      await categoryApi.remove(existing.id);
      useServers.getState().removeCategory(existing.id);
      toast.success('Category deleted.');
      onClose();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={existing ? 'Edit category' : 'Create category'}
      size="sm"
      footer={
        <>
          {existing && (
            <Button variant="ghost" className="mr-auto text-danger!" onClick={() => void remove()}>
              Delete
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="category-form" loading={loading} disabled={!name.trim()}>
            {existing ? 'Save' : 'Create category'}
          </Button>
        </>
      }
    >
      <form id="category-form" onSubmit={submit} className="flex flex-col gap-3">
        <Input label="Category name" value={name} onChange={(e) => setName(e.target.value)} placeholder="New category" maxLength={100} error={fields.name} data-autofocus />
        {error && <p className="text-sm text-danger" role="alert">{error}</p>}
      </form>
    </Modal>
  );
}
